import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { CurrencyNumberInput } from '@/components/ui/currency-input'
import { Input } from '@/components/ui/input'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { TradeDiscountInput } from '@/lib/documentTotals'
import type { TradeDiscountType } from '@/types'
import priceTagIcon from '@/assets/icons/price-tag.svg'

const FIXED_PRESETS = [20, 50, 100] as const
const PERCENT_PRESETS = [5, 10, 15] as const

interface Props {
  value: TradeDiscountInput
  amount: number
  onChange: (value: TradeDiscountInput) => void
  onRemove: () => void
  className?: string
}

export function TradeDiscountEditor({
  value,
  amount,
  onChange,
  onRemove,
  className,
}: Props) {
  const { t } = useTranslation('quotes')
  const type: TradeDiscountType = value.type === 'percent' ? 'percent' : 'fixed'
  const currentValue = value.value ?? 0
  const presets = type === 'fixed' ? FIXED_PRESETS : PERCENT_PRESETS

  const updateType = (nextType: TradeDiscountType) => {
    onChange({
      type: nextType,
      value: nextType === type ? currentValue : 0,
    })
  }

  return (
    <div
      className={cn(
        'rounded-2xl border border-[#e7e9ef] bg-[#f4f5f8] p-3',
        className
      )}
    >
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-1'>
          <span className='flex items-center justify-center'>
            <img
                src={priceTagIcon}
                alt=''
                width={16}
                height={16}
                className='shrink-0'
              />
          </span>
          <h3 className='font-heading text-sm font-semibold uppercase leading-[1.2] text-[#3d434e]'>
            {t('form.tradeDiscountCard.title')}
          </h3>
        </div>

        <div className='flex items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            className='h-7 gap-1 rounded-lg px-1.5 text-xs font-medium text-[#555b6a] hover:bg-white hover:text-[#171a20]'
            onClick={onRemove}
          >
            <X className='size-4' />
            {t('form.tradeDiscountCard.remove')}
          </Button>
        </div>
      </div>

      <div className='mt-3 rounded-2xl bg-[#ecfdf5] p-3'>
        <div className='flex flex-nowrap items-center justify-between gap-2 overflow-x-auto p-1.25'>
          <div className='flex items-center gap-2'>
            <div className='inline-flex shrink-0 rounded-xl border border-[#d7dbe7] bg-white p-1'>
              <TradeDiscountTypeButton
                active={type === 'percent'}
                label='%'
                onClick={() => updateType('percent')}
              />
              <TradeDiscountTypeButton
                active={type === 'fixed'}
                label='€'
                onClick={() => updateType('fixed')}
              />
            </div>

            <div className='relative min-w-[92px] max-w-[113px] flex-1'>
              {type === 'fixed' ? (
                <CurrencyNumberInput
                  value={currentValue}
                  className='h-9 rounded-xl border border-[#d7dbe7] bg-white px-3 pr-8 text-sm font-normal text-[#171a20] shadow-none'
                  onValueChange={nextValue =>
                    onChange({ type, value: nextValue ?? 0 })
                  }
                />
              ) : (
                <Input
                  type='number'
                  min={0}
                  max={100}
                  step='0.01'
                  value={currentValue === 0 ? '' : currentValue}
                  onChange={event =>
                    onChange({
                      type,
                      value: Number(event.target.value || 0),
                    })
                  }
                  className='h-9 rounded-xl border border-[#d7dbe7] bg-white px-3 pr-8 text-sm font-normal text-[#171a20] shadow-none'
                  placeholder={t('form.tradeDiscountCard.percentPlaceholder')}
                />
              )}
              <span className='pointer-events-none absolute inset-y-0 right-3 inline-flex items-center text-sm font-normal text-[#3d434e]'>
                {type === 'fixed' ? '€' : '%'}
              </span>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            {presets.map(preset => (
              <Button
                key={preset}
                type='button'
                variant='outline'
                className='h-9 shrink-0 rounded-xl border border-[#d7dbe7] bg-white px-3 py-2.5 text-sm font-normal text-[#171a20] hover:bg-[#f8fafc]'
                onClick={() => onChange({ type, value: preset })}
              >
                {type === 'fixed' ? `€${preset}` : `${preset}%`}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {amount > 0 ? (
        <p className='mt-3 text-sm font-medium leading-[1.4] text-[#059669]'>
          {t('form.tradeDiscountCard.summary', {
            amount: formatCurrency(amount, 'EUR', 2),
          })}
        </p>
      ) : (
        <p className='mt-3 text-sm font-normal leading-[1.4] text-[#8f95a6]'>
          {t('form.tradeDiscountCard.hint')}
        </p>
      )}
    </div>
  )
}

function TradeDiscountTypeButton({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type='button'
      className={cn(
        'inline-flex h-7 min-w-[30px] items-center justify-center rounded-lg px-2.5 text-sm font-medium text-[#3d434e] transition-colors',
        active && 'bg-[#ff5a1f] text-white'
      )}
      onClick={onClick}
    >
      {label}
    </button>
  )
}

export default TradeDiscountEditor
